import { useQuery } from "react-query";

import * as projectService from "api/services/projectService";
import { oneDayInMs } from "constants/caching";
import { formatToCurrency } from "utils/number";

const projectFundsKey = (projectId) => ["projects", "funds", projectId];

const projectFinancialsDataKey = (projectId) => [
  "projects",
  "financials",
  projectId,
];

const projectInformationKey = (projectId) => [
  "projects",
  "information",
  projectId,
];

const projectValidationInfoKey = (projectId) => [
  "projects",
  "validation",
  projectId,
];

/**
 * Store server's state of all project categories
 *
 * @returns {object} The query object return by the useQuery hook
 */
export const useProjectCategories = () => {
  const selectProjectCategories = (data) => {
    const projectCategories = data?.data;
    const reducedData = projectCategories.reduce(
      (map, obj) => ({
        ...map,
        [obj.id]: obj,
      }),
      {}
    );

    return {
      ...data,
      data: reducedData,
    };
  };

  return useQuery(
    ["projects", "categories"],
    () => projectService.getAllCategories(),
    {
      select: selectProjectCategories,
      staleTime: oneDayInMs,
      refetchOnMount: true,
    }
  );
};

/**
 * Store server's state of project category form
 *
 * @param {string} projectId The id of the project being fetched
 *
 * @returns {object} The query object return by the useQuery hook
 */
export const useProjectCategoryData = (projectId) => {
  const selectProjectCategoryData = (data) => {
    const projectCategoryData = data?.data;
    const reducedFlags =
      projectCategoryData?.flags?.reduce(
        (map, obj) => ({
          ...map,
          [obj.fieldCode]: {
            id: obj.id,
            ownerName: obj.updatedByName,
            ownerEmail: obj.updatedByEmail,
            comment: obj.message,
          },
        }),
        {}
      ) || {};
    return {
      ...data,
      data: {
        ...projectCategoryData,
        categoryId: projectCategoryData?.categoryId ?? "",
        subcategoryId: projectCategoryData?.subcategoryId ?? "",
        changeRequestReason: projectCategoryData?.changeRequestReason ?? "",
        flags: reducedFlags,
      },
    };
  };

  return useQuery(
    ["projects", "category", projectId],
    () => projectService.getCategoryData(projectId),
    {
      select: selectProjectCategoryData,
      enabled: !!projectId && projectId !== "new",
      refetchOnWindowFocus: false,
    }
  );
};

/**
 * Store server's state of project communications form
 *
 * @param {string} projectId The id of the project being fetched
 *
 * @returns {object} The query object return by the useQuery hook
 */
export const useProjectCommunicationsData = (projectId) => {
  const selectProjectCommunicationsData = (data) => {
    const projectCommunicationsData = data?.data;
    const reducedFlags =
      projectCommunicationsData?.flags?.reduce(
        (map, obj) => ({
          ...map,
          [obj.fieldCode]: {
            id: obj.id,
            ownerName: obj.updatedByName,
            ownerEmail: obj.updatedByEmail,
            comment: obj.message,
          },
        }),
        {}
      ) || {};
    return {
      ...(data?.data ?? {}),
      flags: reducedFlags,
    };
  };

  return useQuery(
    ["projects", "communications", projectId],
    () => projectService.getCommunicationsData(projectId),
    {
      select: selectProjectCommunicationsData,
    }
  );
};

/**
 * Store server's state of all project funds
 *
 * @param {string} projectId The id of the project being fetched
 * @param {boolean} isProjectReview Whether the funds are for project review
 *
 * @returns {object} The query object return by the useQuery hook
 */
export const useProjectFunds = (projectId, isProjectReview) => {
  const selectProjectFunds = (data) => {
    const projectFunds = data?.data;
    const mappedFunds = projectFunds?.funds?.map((item) => ({
      year: item.year,
      amount: formatToCurrency(item.amount),
      rawAmount: item.amount,
    }));
    return {
      ...data,
      data: {
        total: projectFunds?.total ?? 0,
        funds: mappedFunds,
      },
    };
  };

  return useQuery(
    projectFundsKey(projectId),
    () => projectService.getFunds(projectId),
    {
      select: selectProjectFunds,
      enabled: !isProjectReview,
    }
  );
};

/**
 * Invalidate project funds query.
 *
 * @param {object} queryClient
 * @param {string} projectId
 */
export const invalidateProjectFunds = (queryClient, projectId) => {
  queryClient.invalidateQueries(projectFundsKey(projectId));
};

/**
 * Store server's state of project financials form
 *
 * @param {string} projectId The id of the project being fetched
 *
 * @returns {object} The query object return by the useQuery hook
 */
export const useProjectFinancialsData = (projectId) => {
  const selectProjectFinancialsData = (data) => {
    const projectFinancialsData = data?.data;
    const reducedFlags =
      projectFinancialsData?.flags?.reduce(
        (map, obj) => ({
          ...map,
          [obj.fieldCode]: {
            id: obj.id,
            ownerName: obj.updatedByName,
            ownerEmail: obj.updatedByEmail,
            comment: obj.message,
          },
        }),
        {}
      ) || {};
    return {
      ...data,
      data: {
        ...projectFinancialsData,
        ccbfFunding: projectFinancialsData.ccbfFunding?.toString() ?? "",
        totalCcbfBudgeted:
          projectFinancialsData.totalCcbfBudgeted?.toString() ?? "",
        totalProjectCost:
          projectFinancialsData.totalProjectCost?.toString() ?? "",
        additionalComments: projectFinancialsData?.additionalComments ?? "",
        flags: reducedFlags,
      },
    };
  };

  return useQuery(
    projectFinancialsDataKey(projectId),
    () => projectService.getFinancialsData(projectId),
    {
      select: selectProjectFinancialsData,
      enabled: !!projectId && projectId !== "new",
    }
  );
};

/**
 * Invalidate project financials query.
 *
 * @param {object} queryClient
 * @param {string} projectId
 */
export const invalidateProjectFinancialsData = (queryClient, projectId) => {
  queryClient.invalidateQueries(projectFinancialsDataKey(projectId));
};

/**
 * Store server's state of project general information form
 *
 * @param {string} projectId The id of the project being fetched
 *
 * @returns {object} The query object return by the useQuery hook
 */
export const useProjectInformationData = (projectId) => {
  const selectProjectInformationData = (data) => {
    const projectInformationData = data?.data;

    const primaryLocation = projectInformationData?.locations?.find(
      (obj) => obj.isPrimary
    );
    const secondaryLocations = projectInformationData?.locations?.filter(
      (obj) => !obj.isPrimary
    );

    const reducedFlags =
      projectInformationData?.flags?.reduce(
        (map, obj) => ({
          ...map,
          [obj.fieldCode]: {
            id: obj.id,
            ownerName: obj.updatedByName,
            ownerEmail: obj.updatedByEmail,
            comment: obj.message,
          },
        }),
        {}
      ) || {};

    return {
      ...data,
      data: {
        ...projectInformationData,
        title: projectInformationData?.title ?? "",
        internalId: projectInformationData?.internalId ?? "",
        objectives: projectInformationData?.objectives ?? "",
        scopeOfWork: projectInformationData?.scopeOfWork ?? "",
        investmentTypeId: projectInformationData?.investmentTypeId ?? "",
        signagePosted: projectInformationData?.signagePosted ?? "",
        additionalComments: projectInformationData?.additionalComments ?? "",
        communicationsActivities:
          projectInformationData?.communicationsActivities ?? "",
        primaryLocation: primaryLocation
          ? {
              id: primaryLocation.id,
              position: {
                lat: primaryLocation.latitude,
                lng: primaryLocation.longitude,
              },
              address: `${primaryLocation.latitude?.toFixed(
                5
              )}, ${primaryLocation.longitude?.toFixed(5)}`,
            }
          : null,
        secondaryLocations:
          secondaryLocations?.map((item) => ({
            id: item.id,
            position: {
              lat: item.latitude,
              lng: item.longitude,
            },
            address: `${item.latitude?.toFixed(5)}, ${item.longitude?.toFixed(
              5
            )}`,
          })) || [],
        flags: reducedFlags,
      },
    };
  };

  return useQuery(
    projectInformationKey(projectId),
    () => projectService.getInformationData(projectId),
    {
      select: selectProjectInformationData,
      enabled: !!projectId && projectId !== "new",
    }
  );
};

/**
 * Invalidate project information query.
 *
 * @param {object} queryClient
 * @param {string} projectId
 */
export const invalidateProjectInformation = (queryClient, projectId) => {
  queryClient.invalidateQueries(projectInformationKey(projectId));
};

/**
 * Store server's state of current project's information to use for UI validation
 *
 * @param {string} projectId The id of the project being fetched
 *
 * @returns The query object return by the useQuery hook
 */
export const useProjectValidationInfo = (projectId) =>
  useQuery(
    projectValidationInfoKey(projectId),
    () => projectService.getValidationInfo(projectId),
    {
      select: ({ data }) => data,
      enabled: !!projectId && projectId !== "new",
    }
  );

/**
 * Invalidate project validation query.
 *
 * @param {object} queryClient
 * @param {string} projectId
 */
export const invalidateProjectValidationInfo = (queryClient, projectId) => {
  queryClient.invalidateQueries(projectValidationInfoKey(projectId));
};
